// SCR-CLS-01/02: four-step owner close and the frozen settlement document for owner and Seller.
import { useCallback, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BottomSheet,
  Button,
  Field,
  FormGrid,
  Input,
  LineAdd,
  List,
  ListRow,
  Notice,
  PaperDoc,
  PaperDocTable,
  PaperParty,
  PaperTotals,
  Section,
  Select,
  SheetActions,
  SkeletonList,
  StageBar,
  SummaryCard,
  Tabs,
} from '../../design/kit';
import { CASH, loadCashPlaces } from '../../data/cash';
import { EXPENSES, categoryLabel, closeRound, loadExpenseCategories, loadRoundClose } from '../../data/expenses';
import { SALES, loadRoundSales, loadSales } from '../../data/sales';
import { loadSettings } from '../../data/settings';
import { useSession } from '../../app/session';
import { useCardHeader } from '../../app/Shell';
import { Code, Name } from '../../i18n/Isolate';
import type { Language } from '../../i18n';
import { formatDateTime, formatMoney } from '../../lib/format';
import { parseAmount, useTab } from '../catalog/common';

interface ExpenseLine {
  category: string;
  amount: string;
}
interface SnapshotInvoice {
  number: string;
  customer: string;
  amount: number;
  profit?: number;
  carried?: boolean;
}
interface SnapshotExpense {
  number: string;
  category: string;
  amount: number;
  notes: string | null;
}
interface CloseSnapshot {
  round: string;
  truck: string;
  seller: string;
  started_at: string;
  closed_at: string;
  paid_invoices: SnapshotInvoice[];
  unpaid_carried: SnapshotInvoice[];
  expenses: SnapshotExpense[];
  expenses_total: number;
  cash_available: number;
  paid_total: number;
  profit_total?: number;
  expected_handover: number;
  handed_over: number;
  variance: number;
  variance_reason: string | null;
  garage_cash_after?: number;
}

export function RoundCloseSheet({
  roundId,
  roundNumber,
  cash,
  own,
  onClose,
}: {
  roundId: string;
  roundNumber: string;
  cash: number;
  /** The owner's own round: no wage and no shortage charged to a Seller account (ADR-136). */
  own: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [lines, setLines] = useState<ExpenseLine[]>([]);
  const [wage, setWage] = useState('0');
  const [handed, setHanded] = useState('');
  const [reason, setReason] = useState('');
  const [shortageTo, setShortageTo] = useState<'seller' | 'business'>(own ? 'business' : 'seller');
  const [problem, setProblem] = useState('');
  const categories = useQuery({ queryKey: [...EXPENSES, 'categories'], queryFn: loadExpenseCategories });
  const paidSales = useQuery({
    queryKey: [...SALES, 'paid-round', roundId],
    queryFn: () => loadSales({ paidRound: roundId }),
  });
  const createdSales = useQuery({
    queryKey: [...SALES, 'round', roundId],
    queryFn: () => loadSales({ round: roundId }),
  });
  const summary = useQuery({ queryKey: [...SALES, 'roundsum', roundId], queryFn: () => loadRoundSales(roundId) });
  const cashPlaces = useQuery({ queryKey: [...CASH, 'places'], queryFn: loadCashPlaces });
  const lineAmounts = lines.map((line) => parseAmount(line.amount) ?? 0);
  const wageAmount = parseAmount(wage) ?? 0;
  const expensesTotal = lineAmounts.reduce((a, b) => a + b, 0) + wageAmount;
  const expected = cash - expensesTotal;
  const parsedHanded = handed.trim() ? parseAmount(handed) : expected;
  const handedAmount = parsedHanded ?? expected;
  const variance = Math.round((handedAmount - expected) * 100) / 100;
  const mutation = useMutation({
    mutationFn: () =>
      closeRound({
        round: roundId,
        expenses: lines.map((line) => ({ category: line.category, amount: parseAmount(line.amount) ?? 0 })),
        wage: wageAmount,
        handedOver: handedAmount,
        varianceReason: reason || null,
        shortageTo,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rounds'] });
      void qc.invalidateQueries({ queryKey: EXPENSES });
      void qc.invalidateQueries({ queryKey: SALES });
      void qc.invalidateQueries({ queryKey: CASH });
      onClose();
      navigate(`/round/closed/${roundId}`);
    },
    onError: () => setProblem(t('err.generic')),
  });
  if (!categories.data || !paidSales.data || !createdSales.data)
    return (
      <BottomSheet title={t('p7.closeRound')} open onClose={onClose}>
        <SkeletonList label={t('loading')} />
      </BottomSheet>
    );
  const availableCategories = categories.data.filter((category) => category.active && !category.fixed);
  const paid = paidSales.data;
  const unpaid = createdSales.data.filter((sale) => sale.status === 'unpaid');
  const steps = [t('p7.close.s1'), t('p7.close.s2'), t('p7.close.s3'), t('p7.close.s4')];
  const proceed = (e: FormEvent) => {
    e.preventDefault();
    setProblem('');
    if (
      step === 1 &&
      (lines.some(
        (line) =>
          !line.category ||
          !parseAmount(line.amount) ||
          (parseAmount(line.amount) ?? 0) <= 0 ||
          Math.round((parseAmount(line.amount) ?? 0) * 100) !== (parseAmount(line.amount) ?? 0) * 100,
      ) ||
        (wage.trim() !== '' &&
          (parseAmount(wage) === null || wageAmount < 0 || Math.round(wageAmount * 100) !== wageAmount * 100)) ||
        expensesTotal > cash)
    ) {
      setProblem(t('err.required'));
      return;
    }
    if (
      step === 2 &&
      (parsedHanded === null ||
        handedAmount < 0 ||
        Math.round(handedAmount * 100) !== handedAmount * 100 ||
        (Math.abs(variance) > 0 && !reason.trim()))
    ) {
      setProblem(t('err.required'));
      return;
    }
    // Entering the handover step: prefill the expected amount once, so the field can then be cleared and retyped.
    if (step === 1 && !handed.trim()) setHanded(String(Math.max(expected, 0)));
    setStep((current) => Math.min(3, current + 1));
  };
  return (
    <BottomSheet title={`${t('p7.closeRound')} · ${roundNumber}`} open onClose={onClose} busy={mutation.isPending}>
      <StageBar label={t('p7.stage', { n: step + 1, total: 4 })} steps={steps} current={step} />
      {step === 0 ? (
        <>
          <Section title={t('p7.close.paid')}>
            <List label={t('p7.close.paid')}>
              {paid.map((sale) => (
                <ListRow
                  key={sale.id}
                  lead={{ icon: 'file' }}
                  title={<Name>{sale.customer}</Name>}
                  sub={
                    <>
                      <Code>{sale.number}</Code> · {t('p7.profit', { amount: formatMoney(sale.margin ?? 0, lang) })}
                    </>
                  }
                  value={formatMoney(sale.total, lang)}
                />
              ))}
              <ListRow
                lead={{ icon: 'cash' }}
                title={t('p7.paidTotal')}
                value={formatMoney(summary.data?.paid_total ?? 0, lang)}
              />
            </List>
          </Section>
          <Section title={t('p7.close.carried')}>
            <List label={t('p7.close.carried')}>
              {unpaid.map((sale) => (
                <ListRow
                  key={sale.id}
                  lead={{ icon: 'file' }}
                  title={<Name>{sale.customer}</Name>}
                  sub={
                    <>
                      <Code>{sale.number}</Code> · {t('p7.close.carried')}
                    </>
                  }
                  value={formatMoney(sale.total, lang)}
                />
              ))}
              {unpaid.length === 0 && <ListRow lead={{ icon: 'file' }} title={t('p7.none')} />}
            </List>
          </Section>
          <Notice tone="info">{t('p7.close.noReturns')}</Notice>
        </>
      ) : step === 1 ? (
        <>
          <section aria-label={t('p7.expensesWage')}>
            {lines.map((line, index) => (
              <LineAdd key={index}>
                <FormGrid>
                  <Field label={t('p7.category')}>
                    {({ id }) => (
                      <Select
                        id={id}
                        value={line.category}
                        onChange={(e) =>
                          setLines((current) =>
                            current.map((v, i) => (i === index ? { ...v, category: e.target.value } : v)),
                          )
                        }
                      >
                        <option value="">—</option>
                        {availableCategories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                  <Field label={t('p7.amount')}>
                    {({ id }) => (
                      <Input
                        id={id}
                        numeric
                        inputMode="decimal"
                        dir="ltr"
                        value={line.amount}
                        onChange={(e) =>
                          setLines((current) =>
                            current.map((v, i) => (i === index ? { ...v, amount: e.target.value } : v)),
                          )
                        }
                      />
                    )}
                  </Field>
                </FormGrid>
                <Button pill onClick={() => setLines((current) => current.filter((_, i) => i !== index))}>
                  {t('p7.remove')}
                </Button>
              </LineAdd>
            ))}
          </section>
          <Button icon="plus" onClick={() => setLines((current) => [...current, { category: '', amount: '' }])}>
            {t('p7.addLine')}
          </Button>
          {own ? (
            <Notice tone="info">{t('own.nowage')}</Notice>
          ) : (
            <Field label={t('p7.wages')}>
              {({ id }) => (
                <Input
                  id={id}
                  numeric
                  inputMode="decimal"
                  dir="ltr"
                  value={wage}
                  onChange={(e) => setWage(e.target.value)}
                />
              )}
            </Field>
          )}
          <Notice tone={expensesTotal > cash ? 'danger' : 'info'}>
            {t('p7.expensesWage')}: {formatMoney(expensesTotal, lang)} · {t('p7.roundCash')}: {formatMoney(cash, lang)}
          </Notice>
        </>
      ) : step === 2 ? (
        <>
          <List label={t('p7.closeRound')}>
            <ListRow lead={{ icon: 'cash' }} title={t('p7.expected')} value={formatMoney(expected, lang)} />
          </List>
          <Field label={t('p7.handed')}>
            {({ id }) => (
              <Input
                id={id}
                numeric
                inputMode="decimal"
                dir="ltr"
                value={handed}
                onChange={(e) => setHanded(e.target.value)}
              />
            )}
          </Field>
          <Notice tone={variance < 0 ? 'danger' : variance > 0 ? 'warning' : 'info'}>
            {t('p7.variance')}: {formatMoney(variance, lang)}
          </Notice>
          {variance !== 0 && (
            <>
              <Field label={t('p7.reason')}>
                {({ id }) => <Input id={id} dir="auto" value={reason} onChange={(e) => setReason(e.target.value)} />}
              </Field>
              {variance < 0 && !own && (
                <Field label={t('p7.shortage')}>
                  {({ id }) => (
                    <Select
                      id={id}
                      value={shortageTo}
                      onChange={(e) => setShortageTo(e.target.value as typeof shortageTo)}
                    >
                      <option value="seller">{t('p7.owedBy')}</option>
                      <option value="business">{t('p7.businessAbsorbs')}</option>
                    </Select>
                  )}
                </Field>
              )}
            </>
          )}
        </>
      ) : (
        <List label={t('p7.close.s4')}>
          <ListRow lead={{ icon: 'file' }} title={t('p7.expensesWage')} value={formatMoney(expensesTotal, lang)} />
          <ListRow lead={{ icon: 'cash' }} title={t('p7.handed')} value={formatMoney(handedAmount, lang)} />
          <ListRow lead={{ icon: 'cash' }} title={t('p7.variance')} sub={reason} value={formatMoney(variance, lang)} />
          <ListRow
            lead={{ icon: 'cash' }}
            title={t('p7.garageAfter')}
            value={
              cashPlaces.data
                ? formatMoney(
                    Number(cashPlaces.data.find((place) => place.kind === 'garage')?.balance ?? 0) + handedAmount,
                    lang,
                  )
                : '—'
            }
          />
        </List>
      )}
      {problem && <Notice tone="danger">{problem}</Notice>}
      <SheetActions>
        {step > 0 && <Button onClick={() => setStep((current) => Math.max(0, current - 1))}>{t('p7.back')}</Button>}
        {step < 3 ? (
          <Button variant="main" onClick={proceed}>
            {t('p7.next')}
          </Button>
        ) : (
          <Button variant="main" busy={mutation.isPending} onClick={() => mutation.mutate()}>
            {t('p7.confirm')}
          </Button>
        )}
      </SheetActions>
    </BottomSheet>
  );
}

export function RoundCloseCard() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const { role } = useSession();
  const [tab, setTab] = useTab(['settlement', 'document'] as const, 'settlement');
  const close = useQuery({ queryKey: [...EXPENSES, 'round-close', id], queryFn: () => loadRoundClose(id) });
  const settings = useQuery({ queryKey: ['settings', 'v_settings'], queryFn: loadSettings });
  const navigate = useNavigate();
  const back = useCallback(() => navigate(-1), [navigate]);
  useCardHeader(close.data?.number ?? t('p.round'), back);
  if (close.isError) return <Notice tone="danger">{t('err.generic')}</Notice>;
  if (!close.data) return <SkeletonList label={t('loading')} />;
  const doc = close.data;
  const snapshot = doc.snapshot as unknown as CloseSnapshot;
  const documentLang = (settings.data?.language ?? lang) as Language;
  const docT = i18n.getFixedT(documentLang);
  // SCR-CLS-02: the frozen round — handed over, with the counted total and what was spent.
  return (
    <>
      <SummaryCard
        label={t('p7.closed')}
        value={formatMoney(doc.handed_over, lang)}
        sub={
          <>
            <Code>{doc.truck_code}</Code> · <Name>{doc.seller_name}</Name> · {formatDateTime(doc.closed_at, lang)}
          </>
        }
        figures={[
          { label: t('p7.paidTotal'), value: formatMoney(doc.paid_total, lang) },
          { label: t('p7.expensesWage'), value: formatMoney(doc.expenses_total, lang) },
        ]}
      />
      <Tabs
        label={doc.number}
        value={tab}
        onChange={(v) => setTab(v as 'settlement' | 'document')}
        items={[
          { id: 'settlement', label: t('p7.tab.settlement') },
          { id: 'document', label: t('p7.tab.document') },
        ]}
      />
      {tab === 'settlement' ? (
        <>
          <List label={t('p7.close.paid')}>
            {snapshot.paid_invoices.map((invoice) => (
              <ListRow
                key={invoice.number}
                lead={{ icon: 'file' }}
                title={<Name>{invoice.customer}</Name>}
                sub={
                  <>
                    <Code>{invoice.number}</Code>
                    {role === 'owner' && invoice.profit !== undefined && (
                      <> · {t('p7.profit', { amount: formatMoney(invoice.profit, lang) })}</>
                    )}
                  </>
                }
                value={formatMoney(invoice.amount, lang)}
              />
            ))}
            <ListRow lead={{ icon: 'cash' }} title={t('p7.paidTotal')} value={formatMoney(doc.paid_total, lang)} />
          </List>
          <List label={t('p7.expensesWage')}>
            {snapshot.expenses.map((expense) => (
              <ListRow
                key={expense.number}
                lead={{ icon: 'file' }}
                title={categoryLabel(t, expense.category, expense.category === 'Wage' ? 'wage' : null)}
                sub={expense.notes ?? undefined}
                value={formatMoney(expense.amount, lang)}
              />
            ))}
            <ListRow
              lead={{ icon: 'cash' }}
              title={t('p7.expected')}
              value={formatMoney(doc.expected_handover, lang)}
            />
            <ListRow lead={{ icon: 'cash' }} title={t('p7.handed')} value={formatMoney(doc.handed_over, lang)} />
            <ListRow
              lead={{ icon: 'cash' }}
              title={t('p7.variance')}
              sub={doc.variance_reason ?? undefined}
              value={formatMoney(doc.variance, lang)}
            />
            <ListRow
              lead={{ icon: 'cash' }}
              title={t('p7.garageAfter')}
              value={formatMoney(snapshot.garage_cash_after ?? 0, lang)}
            />
          </List>
          <List label={t('p7.close.carried')}>
            {snapshot.unpaid_carried.map((invoice) => (
              <ListRow
                key={invoice.number}
                lead={{ icon: 'file' }}
                title={<Name>{invoice.customer}</Name>}
                sub={<Code>{invoice.number}</Code>}
                value={formatMoney(invoice.amount, lang)}
              />
            ))}
            {snapshot.unpaid_carried.length === 0 && <ListRow lead={{ icon: 'file' }} title={t('p7.none')} />}
          </List>
        </>
      ) : (
        <PaperDoc
          businessName={settings.data?.business_name ?? 'YakVender'}
          documentTitle={docT('p7.closeRound')}
          number={<Code>{doc.number}</Code>}
          date={formatDateTime(doc.closed_at, documentLang)}
          party={
            <>
              {docT('p7.truck')}: <Code>{doc.truck_code}</Code> · {docT('tk.seller')}: <Name>{doc.seller_name}</Name> ·{' '}
              {formatDateTime(doc.started_at, documentLang)}
            </>
          }
          stamp={docT('p7.closed')}
        >
          <PaperDocTable>
            <thead>
              <tr>
                <th>{docT('p7.invoice')}</th>
                <th>{docT('p7.customer')}</th>
                <th className="num">{docT('p7.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {snapshot.paid_invoices.map((invoice) => (
                <tr key={invoice.number}>
                  <td>
                    <Code>{invoice.number}</Code>
                  </td>
                  <td>
                    <Name>{invoice.customer}</Name>
                  </td>
                  <td className="num">{formatMoney(invoice.amount, documentLang)}</td>
                </tr>
              ))}
              <tr>
                <td colSpan={2}>{docT('p7.expensesWage')}</td>
                <td className="num">−{formatMoney(doc.expenses_total, documentLang)}</td>
              </tr>
            </tbody>
          </PaperDocTable>
          <PaperTotals>
            <span>
              {docT('p7.expected')}: {formatMoney(doc.expected_handover, documentLang)} · {docT('p7.variance')}:{' '}
              {formatMoney(doc.variance, documentLang)}
            </span>
            <b>
              {docT('p7.handed')}: {formatMoney(doc.handed_over, documentLang)}
            </b>
          </PaperTotals>
          <PaperParty>
            {docT('p7.close.carried')}:{' '}
            {snapshot.unpaid_carried.map((invoice) => (
              <span key={invoice.number}>
                <Code>{invoice.number}</Code> {formatMoney(invoice.amount, documentLang)}{' '}
              </span>
            ))}
          </PaperParty>
        </PaperDoc>
      )}
    </>
  );
}
